import {Ref, ref} from "vue";
import Random from "~/ts/library/Random";
import Delay from "~/ts/library/Delay";
import WebRtcMediaStream from "~/ts/library/webRtc/WebRtcMediaStream";
import WebRtcCallLoggerInterface from "~/ts/library/webRtc/WebRtcCallLoggerInterface";


export class RTCPeerConnectionWrapper {
    private peerConnection?: RTCPeerConnection;
    private localVideoAvailable: Ref<boolean>;
    private remoteStream: Ref<MediaStream | undefined>;
    private connectionState: Ref<RTCPeerConnectionState | undefined>;
    private iceCandidates: string[];
    private uid: string;
    private waitingIceCandidates: string[];
    private wasConnected: boolean = false;
    private senders: RTCRtpSender[];
    private webRtcMediaStream: WebRtcMediaStream;
    private disposeEvent?: () => void;
    private logger: WebRtcCallLoggerInterface;

    constructor(
        private configuration: {
            webRtcMediaStream: WebRtcMediaStream,
            pairId: number,
            iceServers: RTCIceServer[],
            sendIceCandidatesMethod: (candidates: string[]) => void,
            onDisconnect: () => void,
            onConnect: () => void
        },
        logger: WebRtcCallLoggerInterface
    ) {
        this.logger = logger;
        this.webRtcMediaStream = configuration.webRtcMediaStream;
        this.senders = [];
        this.uid = Random.uid();
        this.localVideoAvailable = ref(true);
        this.remoteStream = ref();
        this.iceCandidates = [];
        this.waitingIceCandidates = [];
        this.connectionState = ref();
    }

    public get pairId(): number {
        return this.configuration.pairId;
    }

    public async init() {
        this.logger.addLog('{RTCPeerConnectionWrapper} init');
        this.peerConnection = new RTCPeerConnection({
            iceServers: this.configuration.iceServers
        });
        this.connectionState.value = this.peerConnection.connectionState;
        this.logger.addLog('{RTCPeerConnectionWrapper} initLocalStreamTracks start');
        this.initLocalStreamTracks();
        this.logger.addLog('{RTCPeerConnectionWrapper} initLocalStreamTracks done');

        this.disposeEvent = this.webRtcMediaStream.onMediaStreamChange(() => {
            this.initLocalStreamTracks();
        });



        // Отправка ICE-кандидатов при их обнаружении
        this.peerConnection.addEventListener("icecandidate", event => {
            if (event.candidate) {
                this.addIceCandidate(event.candidate);
            }
        });

        // Получение удаленного потока
        this.peerConnection.addEventListener("track", event => {
            this.remoteStream.value = event.streams[0];
        });

        this.peerConnection.addEventListener("connectionstatechange", () => {
            let state = this.peerConnection?.connectionState;
            this.connectionState.value = state;
            if (state == "failed" || state == "disconnected") {
                if (this.wasConnected) {
                    this.wasConnected = false;
                    this.configuration.onDisconnect();
                }
            } else if (state == "connected") {
                this.wasConnected = true;
                this.configuration.onConnect();
            }
        });
    }

    public async generateOffer(): Promise<string> {
        this.logger.addLog(`{rtcPeerWrapper/generateOffer} start generateOffer`);
        const offer = await this.peerConnection!.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
        });
        this.logger.addLog(`{rtcPeerWrapper/generateOffer} offerCreated`, offer);
        this.logger.addLog(`{rtcPeerWrapper/generateOffer} setLocalDescription`);
        await this.peerConnection!.setLocalDescription(offer);
        this.logger.addLog(`{rtcPeerWrapper/generateOffer} done setLocalDescription`);
        return JSON.stringify(offer);
    }

    public async generateAnswer(offer: string): Promise<string> {
        this.logger.addLog(`{rtcPeerWrapper/generateAnswer} start setRemoteDescription`, offer);
        await this.peerConnection!.setRemoteDescription(
            new RTCSessionDescription(JSON.parse(offer))
        );
        this.logger.addLog(`{rtcPeerWrapper/generateAnswer} start createAnswer`);
        const answer = await this.peerConnection!.createAnswer();
        this.logger.addLog(`{rtcPeerWrapper/generateAnswer} start setLocalDescription`, answer);
        await this.peerConnection!.setLocalDescription(answer);
        this.applyWaitingIceCandidates();

        return JSON.stringify(answer);
    }

    public async receiveAnswer(answer: string): Promise<void> {
        this.logger.addLog(`{rtcPeerWrapper/receiveAnswer}`, answer);
        if (!this.peerConnection!.remoteDescription) {
            this.logger.addLog(`{rtcPeerWrapper/receiveAnswer} !this.peerConnection.remoteDescription; start setRemoteDescription`);
            await this.peerConnection!.setRemoteDescription(
                new RTCSessionDescription(JSON.parse(answer))
            );
            this.logger.addLog(`{rtcPeerWrapper/receiveAnswer} done setRemoteDescription`);

        }
        this.applyWaitingIceCandidates();
    }

    private applyWaitingIceCandidates() {
        if (this.peerConnection!.remoteDescription && this.waitingIceCandidates.length) {
            let candidates = [...this.waitingIceCandidates];
            this.waitingIceCandidates.splice(0, this.waitingIceCandidates.length);
            this.logger.addLog(`{rtcPeerWrapper/applyWaitingIceCandidates}`, candidates);

            this.receiveIceCandidates(candidates);
        }
    }

    private addIceCandidate(candidate: RTCIceCandidate) {
        this.logger.addLog(`{rtcPeerWrapper/addIceCandidate}`, candidate);
        this.iceCandidates.push(JSON.stringify(candidate));
        this.sendIceCandidates();
    }

    public receiveIceCandidates(candidates: string[]): this {
        if (this.peerConnection?.remoteDescription) {
            for (let item of candidates) {
                let parsedItem = JSON.parse(item);
                this.logger.addLog(`{rtcPeerWrapper/receiveIceCandidates} addIceCandidate`, parsedItem);
                this.peerConnection.addIceCandidate(parsedItem);
            }
        } else {
            this.waitingIceCandidates.push(...candidates);
        }
        return this;
    }

    public getRemoteStream() {
        return this.remoteStream.value;
    }

    public getLocalStream(): MediaStream | undefined {
        return this.webRtcMediaStream.getMediaStream();
    }

    public isConnected() {
        return this.connectionState.value == "connected";
    }

    public destroy(): void {
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = undefined;
        }
        if (this.disposeEvent) {
            this.disposeEvent();
        }
        //AbstractMediaStream.removeHolderFromAllStreams(this);
    }

    private async sendIceCandidates(): Promise<void> {
        if (this.iceCandidates.length > 0) {
            try {
                await Delay.make(100, `sendIceCandidates_${this.uid}`, true);
                try {
                    this.configuration.sendIceCandidatesMethod(this.iceCandidates);
                } catch (e) {
                    console.error(e);
                }
            } catch (e) {

            }
        }
    }

    private getSender(kind: string) {
        return this.senders.find(sender => sender.track?.kind == kind);
    }

    private setTrack(track: MediaStreamTrack, stream: MediaStream) {
        let sender = this.getSender(track.kind);
        if (sender) {
            //console.log("replace track", sender, track);
            sender.replaceTrack(track);
        } else {
            this.senders.push(
                this.peerConnection!.addTrack(track, stream)
            );
            //console.log("addTrack", track, stream, this.senders, this);
        }
    }

    private initLocalStreamTracks() {
        let stream = this.webRtcMediaStream.getMediaStream();
        //console.log("!!!! initLocalStreamTracks !!!!", stream, this);
        if (!stream) {
            debugger;
        }

        stream?.getTracks().forEach(track => {
            this.setTrack(track, stream);
        });
    }

    public isLocalVideoAvailable(): boolean {
        return this.localVideoAvailable.value;
    }
}